import React, { useState, useEffect, FunctionComponent } from 'react';
import { createFragmentContainer, graphql, Environment } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import Contact1stdibsModalLazy from './Contact1stdibsModalLazy';
import PdpRespConsumerSourcingTooltipLoader from '../PdpRespConsumerSourcingTooltip/PdpRespConsumerSourcingTooltipLoader';

import styles from './Contact1stdibsLink.scss';

import { Contact1stdibsLink_viewer as ViewerType } from './__generated__/Contact1stdibsLink_viewer.graphql';

const TradeRepCircle = React.lazy(() =>
    import(
        /* webpackChunkName: "Contact1stdibsModalTradeRepCircle" */ 'dibs-sales-rep/exports/TradeRepCircle'
    )
);

type Contact1stdibsLinkProps = {
    hasUserId: boolean;
    hasRootPendingRefetch: boolean;
    isUserTypeTrade: boolean;
    isUserTypeVip: boolean;
    viewer: ViewerType;
    isConsumerSourcingTooltipVisible: boolean;
    userId: string;
    environment: Environment;
};

const Contact1stdibsLink: FunctionComponent<Contact1stdibsLinkProps> = ({
    hasUserId,
    hasRootPendingRefetch,
    isUserTypeTrade,
    isUserTypeVip,
    viewer,
    isConsumerSourcingTooltipVisible,
    userId,
    environment,
}) => {
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [didMount, setDidMount] = useState(false);
    // shortcut to get around filling out localStorage
    // to trigger consumer sourcing tooltip
    const [forceConsumerSourcingTooltip, setForceConsumerSourcingTooltip] = useState(false);
    useEffect(() => {
        setDidMount(true);
        const queryParams = new URLSearchParams(window.location.search);
        // 'modal' is a slight misnomer but using an existing
        // query param name intead of adding an additional one
        // mainly meant for testing purposes
        setForceConsumerSourcingTooltip(queryParams.get('modal') === 'consumerSourcing');
    }, []);
    const renderTradeCirlce = isUserTypeTrade && hasUserId && didMount && !hasRootPendingRefetch;
    const showConsumerSourcingTooltip =
        // regular PC condition
        (isConsumerSourcingTooltipVisible && !!hasUserId && !isUserTypeTrade && !isUserTypeVip) ||
        // debug/testing condition
        (isConsumerSourcingTooltipVisible && forceConsumerSourcingTooltip);

    return (
        <>
            <div className={styles.wrapper}>
                <button
                    className={styles.container}
                    onClick={() => {
                        setIsContactModalOpen(true);
                    }}
                >
                    {renderTradeCirlce ? (
                        <div className={styles.iconWrapper}>
                            <React.Suspense fallback={null}>
                                <TradeRepCircle theme="dark" viewer={viewer} />
                            </React.Suspense>
                        </div>
                    ) : null}
                    {/* element below is used as an anchor for `ContactTradeRepTooltip.tsx`, hence the ID */}
                    <div className={styles.contact1stdibs} id="trade-spec">
                        <FormattedMessage
                            id="dbl.contact1stdibsLink.contact1stdibs"
                            defaultMessage="Contact 1stdibs"
                        />
                    </div>
                </button>

                {showConsumerSourcingTooltip && (
                    <PdpRespConsumerSourcingTooltipLoader
                        userId={userId}
                        environment={environment}
                        forceConsumerSourcingTooltip={forceConsumerSourcingTooltip}
                    />
                )}
            </div>

            <Contact1stdibsModalLazy
                isOpen={isContactModalOpen}
                placement="globalNav"
                onClose={() => setIsContactModalOpen(false)}
            />
        </>
    );
};

export default createFragmentContainer(Contact1stdibsLink, {
    viewer: graphql`
        fragment Contact1stdibsLink_viewer on Viewer {
            ...TradeRepCircle_viewer @include(if: $hasTradeUserId)
        }
    `,
});
